<template>
  <div class="template-form-builder-page">
    <CardEditSubHeader
      :type="'formBuilder-template-edit'"
      :card="state.card"
      :hideActions="true"
    />
    <div class="template-form-header">
      <div class="field template-form-field">
        <label class="label has-text-black-bis">Template Name</label>
        <div class="control">
          <input
            v-model.trim="state.card.templateName"
            type="text"
            class="input"
            placeholder="Enter template name"
          />
        </div>
      </div>

      <div class="field template-form-field">
        <label class="label has-text-black-bis">Template ID</label>
        <div class="control">
          <input
            v-model.trim="state.card.templateId"
            type="text"
            class="input"
            placeholder="Enter template ID"
          />
        </div>
      </div>
    </div>

    <div class="template-form-builder">
      <FormBuilder
        v-if="state.loaded"
        ref="form"
        :key="state.formKey"
        :cardId="state.cardId"
        :templateForm="state.card"
        :editForm="state.editForm"
      />
    </div>
    
    <div class="template-form-footer" v-if="state.hasPermission">
      <button type="button" class="button is-cancel is-outlined" @click="clearFormData">
        Clear
      </button>
      <button type="button" class="button has-background-black-bis has-text-white" @click="previewFormData">
        {{ state.preview ? 'Edit' : 'Preview' }}
      </button>
      <button type="button" class="button is-success" @click="saveCard">
        Save
      </button>
      <button type="button" class="button is-outlined" @click="cancel">
        Cancel
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, getCurrentInstance, onBeforeMount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { debounce, isEmpty } from 'lodash';

import Todo from '@/models/Todo';
import FormBuilder from '@/components/FormBuilder.vue';
import CardEditSubHeader from '@/components/card-edit/CardEditSubheader.vue';
import { useToast } from 'vue-toastification';
import PermissionMixin from '@/components/mixins/PermissionMixin';

const toast = useToast();
const route = useRoute();
const router = useRouter();
const store = useStore();

const { proxy } = getCurrentInstance();
const { getPermissionValues } = PermissionMixin();

const form = ref(null);

const state = reactive({
  hasSaved: false,
  activeTable: 0,
  editForm: true,
  loaded: false,
  preview: false,
  formKey: 0,
  cardId: route.params.formId,
  card: {
    templateName: '',
    templateId: '',
    isManager: false,
    project: null,
    cardType: 'templateForm',
    company: store.state.userData.company,
    formData: '',
  },
  hasPermission: true
});

const savePromise = () => {
  return new Promise((resolve) => {
    const inter = setInterval(() => {
      if (state.hasSaved) {
        clearInterval(inter);
        resolve();
      }
    }, 150);
  });
};

const showError = (message) => {
  toast.warning(message);
};

const validateForm = () => {
  if (isEmpty(state.card.templateName)) {
    toast.warning('Please Enter Template Name');
    return false;
  }

  if (state.card.templateName.trim().length < 3) {
    toast.warning('Template Name should be minimum of three characters');
    return false;
  }

  const templateId = (state.card.templateId || '').trim();
  if (templateId.length < 4 || templateId.length > 16) {
    toast.warning('Template Id should have a Minimum of 4 Characters and Maximum of 16 Characters');
    return false;
  }

  if (!state.card.formData.length) {
    toast.warning('Please Enter Form Data');
    return false;
  }

  return true;
};

const saveCard = async () => {
  const message = !state.card.formData
    ? 'New form added'
    : 'Changes Saved!';

  state.card.formData = form.value.save();

  if (validateForm()) {
    const response = await Todo.updateFormTemplate(state.card, false);
    Object.assign(state.card, response.data);

    toast.success(message);
    syncRouteAfterSave();

    return false;
  }

  return { error: true };
};

const clearFormData = () => {
  form.value.clear();
};

const previewFormData = () => {
  if (state.preview) {
    form.value.edit();
  } else {
    form.value.preview();
  }

  state.preview = !state.preview;
};

const cancel = () => {
  router.push({
    path: '/settings/company/templates/formTemplates',
  });
};

const syncRouteAfterSave = () => {
  if (state.cardId === 'add') {
    state.cardId = state.card._id;
    router.replace({
      path: `/formTemplate/${state.card.company}/${state.card._id}`,
    });
  }
};

const update = debounce(async () => {
  if (validateForm()) {
    const response = await Todo.updateFormTemplate(state.card, false);
    Object.assign(state.card, response.data);

    toast.success('Changes saved!');
  }
}, 300);

onBeforeMount(async () => {
  state.hasPermission = !(getPermissionValues({
    permissionModule: 'app&projects',
    type: 'company_templates',
  }))
  if (state.cardId !== 'add') {
    const response = await Todo.getFormBuilder({
      cardId: state.cardId,
    });

    Object.assign(state.card, response);
  }

  state.loaded = true;

});
</script>

<style scoped>
.template-form-builder-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f2f2f2;
}

.template-form-header {
  display: flex;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e5e5;
}

.template-form-field {
  flex: 1;
  margin-bottom: 0;
}

.template-form-builder {
  /* flex: 1; */
  overflow: auto;
  padding: 1rem 1.25rem 0;
}

.template-form-footer {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e5e5e5;
  background-color: #f2f2f2;
}
</style>